import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  HealthAndSafety as HealthIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { mcpService, HealthStatus, type McpServerDto, type McpServerAgentAssignmentDto } from '../services/mcpService';
import { useNotificationStore } from '../store/notificationStore';
import { CodeEditor } from '../components/common/CodeEditor';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';

const healthStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case HealthStatus.Healthy:
      return 'success';
    case HealthStatus.Unhealthy:
      return 'error';
    case HealthStatus.Degraded:
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * MCP server detail page with Overview, Tools, and Agents tabs.
 */
export const McpServerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [server, setServer] = useState<McpServerDto | null>(null);
  const [agents, setAgents] = useState<McpServerAgentAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const loadServer = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await mcpService.getMcpServer(id);
      setServer(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    if (!id) return;
    try {
      const list = await mcpService.getMcpServerAgents(id);
      setAgents(list);
    } catch {
      setAgents([]);
    }
  };

  useEffect(() => {
    loadServer();
  }, [id]);

  useEffect(() => {
    if (id && server) {
      loadAgents();
    }
  }, [id, server?.id]);

  const handleDiscoverTools = async () => {
    if (!id) return;
    setDiscovering(true);
    try {
      const result = await mcpService.discoverTools(id);
      addNotification(`Discovered ${result.toolsDiscovered} tools`, 'success');
      loadServer();
    } catch (err: any) {
      addNotification(err.message || 'Failed to discover tools', 'error');
    } finally {
      setDiscovering(false);
    }
  };

  const handleCheckHealth = async () => {
    if (!id) return;
    setCheckingHealth(true);
    try {
      const result = await mcpService.checkHealth(id);
      if (result.isHealthy) {
        addNotification('Server is healthy', 'success');
      } else {
        addNotification(`Health check failed: ${result.errorMessage || 'Unknown error'}`, 'warning');
      }
      loadServer();
    } catch (err: any) {
      addNotification(err.message || 'Failed to check health', 'error');
    } finally {
      setCheckingHealth(false);
    }
  };

  const handleDelete = async () => {
    if (!server) return;
    if (!window.confirm(`Are you sure you want to delete "${server.name}"?`)) return;
    try {
      await mcpService.deleteMcpServer(server.id);
      addNotification('MCP server deleted successfully', 'success');
      navigate('/mcp-servers');
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to delete MCP server', 'error');
    }
  };

  const parseHeadersJson = (json: string | undefined): Record<string, string> => {
    if (!json || !json.trim()) return {};
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    } catch {
      return {};
    }
  };

  return (
    <PageDetailLayout
      title={server?.name || 'MCP Server'}
      breadcrumbs={[{ label: 'MCP Servers', href: '/mcp-servers' }, { label: server?.name || 'MCP Server' }]}
      backTo={{ label: 'MCP Servers', href: '/mcp-servers' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={server ? { label: server.isActive ? 'Active' : 'Inactive', color: server.isActive ? 'success' : 'error' } : undefined}
      chips={server ? [
        { label: server.healthStatus, color: healthStatusColor(server.healthStatus) },
        { label: `${server.toolCount} tools`, variant: 'outlined' as const },
      ] : []}
      actions={server ? [
        { icon: discovering ? <CircularProgress size={20} /> : <ScienceIcon />, label: 'Discover Tools', onClick: handleDiscoverTools, disabled: discovering },
        { icon: checkingHealth ? <CircularProgress size={20} /> : <HealthIcon />, label: 'Check Health', onClick: handleCheckHealth, disabled: checkingHealth },
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/mcp-servers/${server.id}/edit`) },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
      ] : []}
      tabs={server ? [
        {
          label: 'Overview',
          value: 'overview',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {server.description || 'No description available'}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
                Details
              </Typography>
              <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, mt: 2 }}>
                <Typography component="dt" fontWeight={500}>Server URL:</Typography>
                <Typography component="dd" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{server.serverUrl}</Typography>
                <Typography component="dt" fontWeight={500}>Health status:</Typography>
                <Typography component="dd">
                  <Chip label={server.healthStatus} color={healthStatusColor(server.healthStatus)} size="small" />
                </Typography>
                <Typography component="dt" fontWeight={500}>Last health check:</Typography>
                <Typography component="dd" color="text.secondary">
                  {server.lastHealthCheckAt ? new Date(server.lastHealthCheckAt).toLocaleString() : 'Never'}
                </Typography>
                <Typography component="dt" fontWeight={500}>Tools discovered:</Typography>
                <Typography component="dd" color="text.secondary">{server.toolCount}</Typography>
              </Box>

              {server.headersJson && (() => {
                const headers = parseHeadersJson(server.headersJson);
                const entries = Object.entries(headers);
                if (entries.length === 0) return null;
                return (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
                      Custom Headers
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <CodeEditor value={JSON.stringify(headers, null, 2)} language="json" height="120px" readOnly />
                    </Box>
                  </>
                );
              })()}
            </Box>
          ),
        },
        {
          label: 'Tools',
          value: 'tools',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Discovered Tools ({server.tools?.length ?? server.toolCount ?? 0})
              </Typography>
              {server.tools && server.tools.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tool name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Discovered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {server.tools.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.toolName}</TableCell>
                        <TableCell>{t.description || '—'}</TableCell>
                        <TableCell>{t.discoveredAt ? new Date(t.discoveredAt).toLocaleString() : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tools discovered yet. Use &quot;Discover Tools&quot; to fetch tools from the server.
                </Typography>
              )}
            </Box>
          ),
        },
        {
          label: 'Agents',
          value: 'agents',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Agents using this server
              </Typography>
              {agents.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agents.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Link component="button" variant="body2" onClick={() => navigate(`/agents/${a.agentDefinitionId}`)}>
                            {a.agentName}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip label={a.isEnabled ? 'Enabled' : 'Disabled'} color={a.isEnabled ? 'success' : 'default'} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No agents are assigned to this MCP server. Assign from an agent&apos;s MCP Servers tab.
                </Typography>
              )}
            </Box>
          ),
        },
      ] : []}
    />
  );
};
